import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { IMG_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const MeterModuleList = () => {
  const [meterModules, setMeterModules] = useState([]);
  const [filteredMeterModules, setFilteredMeterModules] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Helper function to format date in DD/MM/YYYY
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
  };

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch staff list
        const staffResponse = await fetch(`${BASE_URL}staff`);
        const staffData = await staffResponse.json();

        let staffListData = [];
        if (staffData.status === 1 && staffData.data) {
          staffListData = staffData.data;
          setStaffList(staffListData);
        }

        // Fetch meter modules
        const meterResponse = await fetch(`${BASE_URL}metermodule`);
        const meterData = await meterResponse.json();

        if (meterData.status === 1 && meterData.data) {
          // Map staff names to meter modules
          const modulesWithStaffNames = meterData.data.map((module) => {
            const staff = staffListData.find((s) => s.id == module.staff_id);
            return {
              ...module,
              staff_name: staff ? staff.name : `Staff ID: ${module.staff_id}`,
            };
          });

          setMeterModules(modulesWithStaffNames);
          setFilteredMeterModules(modulesWithStaffNames);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get staff name by ID
  const getStaffName = (staffId) => {
    if (!staffId || !staffList.length) return "N/A";
    const staff = staffList.find((s) => s.id == staffId);
    return staff ? staff.name : `Staff ID: ${staffId}`;
  };

  // Filter function for both search term and date range
  const filterMeterModules = (term, start, end) => {
    const termLower = term.toLowerCase();
    const filtered = meterModules.filter((module) => {
      // Search in multiple fields including staff name
      const matchesSearch =
        module.in_reading?.toString().includes(term) ||
        module.out_reading?.toString().includes(term) ||
        module.date?.toLowerCase().includes(termLower) ||
        module.staff_name?.toLowerCase().includes(termLower);

      if (module.date) {
        const moduleDate = new Date(module.date).setHours(0, 0, 0, 0);
        const startDateObj = start
          ? new Date(start).setHours(0, 0, 0, 0)
          : null;
        const endDateObj = end ? new Date(end).setHours(0, 0, 0, 0) : null;

        const isInRange =
          (!startDateObj || moduleDate >= startDateObj) &&
          (!endDateObj || moduleDate <= endDateObj);

        return matchesSearch && isInRange;
      }

      return matchesSearch;
    });

    setFilteredMeterModules(filtered);
  };

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterMeterModules(term, startDate, endDate);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleApplyFilter = () => {
    filterMeterModules(searchTerm, startDate, endDate);
  };

  const handleEdit = (id) => {
    navigate(`/EditMeterModule/${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this meter module?")) {
      fetch(`${BASE_URL}metermodule/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            setMeterModules(meterModules.filter((module) => module.id !== id));
            setFilteredMeterModules(
              filteredMeterModules.filter((module) => module.id !== id)
            );
          }
        })
        .catch((error) => console.error("Error deleting meter module:", error));
    }
  };

  const calculateTotalReading = (inReading, outReading) => {
    if (typeof inReading === "number" && typeof outReading === "number") {
      return Math.abs(inReading - outReading);
    } else {
      return "N/A";
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Meter Module List", 14, 10);

    const tableData = filteredMeterModules.map((module) => [
      module.staff_name || getStaffName(module.staff_id) || "N/A",
      module.in_reading || "N/A",
      module.out_reading || "N/A",
      formatDate(module.date),
      calculateTotalReading(module.in_reading || 0, module.out_reading || 0),
      module.in_image ? "Yes" : "No",
      module.out_image ? "Yes" : "No",
    ]);

    const headers = [
      "Staff Name",
      "In Reading",
      "Out Reading",
      "Date",
      "Total Reading",
      "Has In Image",
      "Has Out Image",
    ];

    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: 20,
      theme: "grid",
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
      },
      margin: { top: 20 },
    });

    doc.save("meter_module_list.pdf");
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredMeterModules.map((module) => ({
        "Staff Name":
          module.staff_name || getStaffName(module.staff_id) || "N/A",
        "In Reading": module.in_reading || "N/A",
        "Out Reading": module.out_reading || "N/A",
        Date: formatDate(module.date),
        "Total Reading":
          calculateTotalReading(
            module.in_reading || 0,
            module.out_reading || 0
          ) || "N/A",
        "In Image": module.in_image
          ? `${IMG_URL}${module.in_image}`
          : "No Image",
        "Out Image": module.out_image
          ? `${IMG_URL}${module.out_image}`
          : "No Image",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Meter Module List");
    XLSX.writeFile(workbook, "meter_module_list.xlsx");
  };

  if (isLoading) {
    return (
      <div id="main">
        <div className="page-heading">
          <h3>Meter Module List</h3>
        </div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-3">Loading meter modules...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Meter Module List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <h5>
                      Total Reading:{" "}
                      {filteredMeterModules.reduce(
                        (total, module) =>
                          total +
                          calculateTotalReading(
                            module.in_reading || 0,
                            module.out_reading || 0
                          ),
                        0
                      )}
                    </h5>
                  </div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/AddMeterModule")}
                    >
                      Add Meter Module
                    </button>
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    <button
                      className="btn btn-success ms-2"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Row 1: Search + Dates */}
                <div className="row mb-3 align-items-end">
                  {/* Search */}
                  <div className="col-md-4">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Staff Name, Readings or Date
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff name, readings or date"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>

                  {/* Start Date */}
                  <div className="col-md-4">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) =>
                        handleDateChange(e.target.value, endDate)
                      }
                    />
                  </div>

                  {/* End Date */}
                  <div className="col-md-4">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) =>
                        handleDateChange(startDate, e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Row 2: Apply Filter Button */}
                <div className="row mb-3">
                  <div className="col-md-2">
                    <button
                      className="btn btn-secondary w-100"
                      onClick={handleApplyFilter}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th>
                        <th>In Reading</th>
                        <th>Out Reading</th>
                        <th>Date</th>
                        <th>In Image</th>
                        <th>Out Image</th>
                        <th>Total Reading</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMeterModules.length > 0 ? (
                        filteredMeterModules.map((module) => (
                          <tr key={module.id}>
                            <td>
                              {module.staff_name ||
                                getStaffName(module.staff_id) ||
                                "N/A"}
                            </td>
                            <td>{module.in_reading || "N/A"}</td>
                            <td>{module.out_reading || "N/A"}</td>
                            <td>{formatDate(module.date)}</td>
                            <td>
                              {module.in_image ? (
                                <img
                                  src={`${IMG_URL}${module.in_image}`}
                                  alt="In Reading"
                                  width="100"
                                  height="100"
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: "5px",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://via.placeholder.com/100x100?text=No+Image";
                                  }}
                                />
                              ) : (
                                "No Image"
                              )}
                            </td>
                            <td>
                              {module.out_image ? (
                                <img
                                  src={`${IMG_URL}${module.out_image}`}
                                  alt="Out Reading"
                                  width="100"
                                  height="100"
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: "5px",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://via.placeholder.com/100x100?text=No+Image";
                                  }}
                                />
                              ) : (
                                "No Image"
                              )}
                            </td>
                            <td>
                              {calculateTotalReading(
                                module.in_reading || 0,
                                module.out_reading || 0
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-primary me-2"
                                onClick={() => handleEdit(module.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => handleDelete(module.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No meter modules found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MeterModuleList;
